import { ScreenshotFrameSettingsComponent } from './shared/screenshot-frame-settings/screenshot-frame-settings.component';
import { DatePipe } from "@angular/common";
import {
  HttpXhrBackend,
  provideHttpClient,
  withInterceptorsFromDi,
} from "@angular/common/http";
import { NgModule, inject, provideAppInitializer } from "@angular/core";
import { BrowserModule } from "@angular/platform-browser";
import { BrowserAnimationsModule } from "@angular/platform-browser/animations";
import { NgxPaginationModule } from "ngx-pagination";
import { ShotSettingsService } from "./projects/services/shot-settings.service";

import { AppRoutingModule } from "./app-routing.module";
import { AppComponent } from "./app.component";
import { BannerComponent } from "./app/banner/banner.component";
import { FooterComponent } from "./app/footer/footer.component";
import { NavBarComponent } from "./app/nav-bar/nav-bar.component";
import { ProjectViewComponent } from "./projects/project-view/project-view.component";
import { ParameterGlossaryComponent } from "./projects/project-view/parameter-glossary/parameter-glossary.component";
import { ProjSideBarComponent } from "./projects/project-view/sidebar/sidebar.component";
import { DataComponent } from "./projects/project/data/data.component";
import { FilesComponent } from "./projects/project/files/files.component";
import {
  FindJobIndexPipe,
  ProjectComponent,
} from "./projects/project/project.component";
import { RunfileComponent } from "./projects/project/runfile/runfile.component";
import { SidebarComponent } from "./projects/project/sidebar/sidebar.component";
import { ProjectsComponent } from "./projects/projects.component";

import { InputNumberDirective } from "./directives/input-numbers/input-number.directive";
import { NoScrollNumberDirective } from "./directives/input-numbers/no-scroll-number.directive";
import { CostsComponent } from "./projects/project/costs/costs.component";
import { LoginComponent } from "./login/login.component";

import { AppConfig } from "./app.config";
import { CallbackComponent } from "./callback/callback.component";
import { CachedPaletteService } from "./projects/services/cached-palette.service";
import { PaletteService } from "./projects/services/palette.service";
import { AuthGuardService } from "./shared/services/auth-guard.service";
import { AuthService } from "./shared/services/auth.service";
import { CacheService } from "./shared/services/cache.service";
import { CachedUserService } from "./shared/services/cached-user.service";
import { DefaultMappingService } from "./shared/services/default-mapping.service";
import { ImageLoaderService } from "./shared/services/image-loader.service";
import { UserService } from "./shared/services/user.service";

import { Router } from "@angular/router";
import { EpicmodCommentsComponent } from "./projects/project/dialogs/epicmod-comments/epicmod-comments.component";
import { JobDeleteComponent } from "./projects/project/dialogs/job-delete/job-delete.component";
import { KeepJobComponent } from "./projects/project/dialogs/keep-job/keep-job.component";
import { ProjectNameComponent } from "./projects/project/dialogs/project-name/project-name.component";
import { RebuildDataTableComponent } from "./projects/project/dialogs/rebuild-data-table/rebuild-data-table.component";
import { RebuildTableComponent } from "./projects/project/dialogs/rebuild-project-table/rebuild-project-table.component";
import { VelocityComponent } from "./projects/project/dialogs/velocity/velocity.component";
import { HttpService } from "./shared/services/http.service";

import { StoredSettingsService } from "./projects/services/stored-settings.service";

import { GraphComponent } from "./projects/project/plots/graph/graph.component";
import { PlotsComponent } from "./projects/project/plots/plots.component";
import { ShotImageComponent } from "./projects/project/shot-image/shot-image.component";
import { CompareGraphComponent } from "./projects/project/plots/compare-graph/compare-graph.component";
import { MultiGraphComponent } from "./projects/project/plots/multi-graph/multi-graph.component";

import { ColorPickerModule } from "ngx-color-picker";
import { ProjectModule } from "./projects/project.module";
import { ParamValueMapService } from "./projects/project/data/paramValueMapService";
import { JobDetailComponent } from "./projects/project/dialogs/job-detail/job-detail.component";
import { ParameterViewEditDialogComponent } from "./projects/project/dialogs/parameter-view-edit-dialog/parameter-view-edit-dialog.component";
import { RebuildModelTableComponent } from "./projects/project/dialogs/rebuild-model-table/rebuild-model-table.component";
import { ShowAllFiltersDialogComponent } from "./projects/project/dialogs/show-all-filters-dialog/show-all-filters-dialog.component";
import { ShowMatchingJobsOptionsComponent } from "./projects/project/dialogs/show-matching-jobs-options/show-matching-jobs-options.component";
import { SearchPipe } from "./projects/project/files/search.pipe";
import { SliceSettingsService } from "./projects/services/slice-settings.service";
import { MaterialModule } from "./shared/material.module";
import { MapkeyPipe } from "./shared/pipes/mapkey/mapkey.pipe";
import { ScoresService } from "./shared/services/scores.service";
import { SharedModule } from "./shared/shared.module";
import { ZorroModule } from "./shared/zorro.module";
import { NgxEchartsModule } from "ngx-echarts";
import * as echarts from "echarts/core";
import { CostsModule } from "./costs/costs.module";
import { Ec2InstancesModule } from "./ec2-instances/ec2-instances.module";
import { CopilotModule } from "./copilot/copilot.module";
import { JobOuputRouteComponent } from "./job-ouput-route/job-ouput-route.component";
import { ContactAdminComponent } from "./contact-admin/contact-admin.component";
import { ProjectNewComponent } from "./projects/project/project-new/project-new.component";
import { ModelAiPanelComponent } from "./projects/project/model-new/model-ai-panel/model-ai-panel.component";

import { CanvasRenderer } from "echarts/renderers";
import { TooltipComponent, LegendComponent, ToolboxComponent, TitleComponent, GridComponent, DataZoomComponent, DataZoomInsideComponent, MarkLineComponent, MarkAreaComponent } from 'echarts/components';
import { TreeChart, LineChart, ScatterChart, BarChart } from 'echarts/charts';
import { ModelNewModule } from "./projects/project/model-new/model-new.module";
import { SegyViewerModule } from "./projects/project/segy/segy-viewer/segy-viewer.module";
import { MonacoEditorModule } from "ngx-monaco-editor-v2";
import { monacoConfig } from "./shared/monaco-editor/monaco-setup";

// BarChart + MarkLineComponent are needed by the copilot's native cost &
// convergence charts (block dividers use markLine).
echarts.use([TreeChart, MarkAreaComponent, TooltipComponent, ScatterChart, LegendComponent, ToolboxComponent, CanvasRenderer, LineChart, BarChart, GridComponent, TitleComponent, DataZoomComponent, DataZoomInsideComponent, MarkLineComponent]);

export function initializeApp(appConfig: AppConfig) {
  return () => appConfig.load();
}

export function httpServiceFactory(
  backend: HttpXhrBackend,
  router: Router,
  authService: AuthService,
) {
  return new HttpService(backend, router, authService);
}

@NgModule({
  declarations: [
    SearchPipe,
    FindJobIndexPipe,
    AppComponent,
    NavBarComponent,
    BannerComponent,
    FooterComponent,
    ProjectsComponent,
    ProjectComponent,
    ProjectViewComponent,
    SidebarComponent,
    ProjSideBarComponent,
    DataComponent,
    RunfileComponent,
    FilesComponent,
    InputNumberDirective,
    NoScrollNumberDirective,
    LoginComponent,
    CallbackComponent,
    CostsComponent,
    ProjectNameComponent,
    JobDeleteComponent,
    JobDetailComponent,
    VelocityComponent,
    EpicmodCommentsComponent,
    KeepJobComponent,
    RebuildTableComponent,
    RebuildDataTableComponent,
    RebuildModelTableComponent,
    ParameterViewEditDialogComponent,
    ShowMatchingJobsOptionsComponent,
    ShowAllFiltersDialogComponent,
    PlotsComponent,
    GraphComponent,
    ShotImageComponent,
    CompareGraphComponent,
    MultiGraphComponent,
    MapkeyPipe,
    JobOuputRouteComponent,
    ContactAdminComponent,
    ProjectNewComponent,
  ],
  exports: [],
  bootstrap: [AppComponent],
  imports: [
    BrowserAnimationsModule,
    BrowserModule,
    SharedModule,
    MaterialModule,
    Ec2InstancesModule,
    ZorroModule,
    NgxPaginationModule,
    AppRoutingModule,
    CostsModule,
    ProjectModule,
    ModelNewModule,
    ScreenshotFrameSettingsComponent,
    SegyViewerModule,
    ColorPickerModule,
    CopilotModule,
    ModelAiPanelComponent,
    ParameterGlossaryComponent,
    NgxEchartsModule.forRoot({ echarts }),
    MonacoEditorModule.forRoot(monacoConfig),
  ],
  providers: [
    AppConfig,
    provideAppInitializer(() => {
      const initializerFn = initializeApp(inject(AppConfig));
      return initializerFn();
    }),
    {
      provide: HttpService,
      useFactory: httpServiceFactory,
      deps: [HttpXhrBackend, Router, AuthService],
    },
    AuthService,
    ScoresService,
    ParamValueMapService,
    AuthGuardService,
    CacheService,
    PaletteService,
    CachedPaletteService,
    ImageLoaderService,
    UserService,
    CachedUserService,
    DefaultMappingService,
    StoredSettingsService,
    SliceSettingsService,
    ShotSettingsService,
    DatePipe,
    provideHttpClient(withInterceptorsFromDi()),
  ],
})
export class AppModule {}
